import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Deep import, NOT the `@/features/organization` barrel: the barrel pulls in
// the router, which breaks every router-free test that transitively imports
// this component (e.g. sales-pages.test.tsx).
import { useActiveOrganization } from '@/features/organization/api/get-active-organization';
import { cn } from '@/lib/utils';

import { type AppointmentStatus, appointmentStatusLabels } from '../api/types';
import { useUpdateAppointment } from '../api/update-appointment';
import { appointmentEditForm } from '../edit';
import type { DepositSummary } from './deposit-badge';
import { ForfeitConfirmDialog } from './forfeit-confirm-dialog';
import { type ForfeitAction, wouldForfeitDeposit } from './forfeit-decision';

/**
 * Front-desk statuses selectable from the appointment detail. Ordered as the
 * booking progresses, with the exception outcomes (no-show, cancelled) last.
 */
export const APPOINTMENT_STATUS_OPTIONS: AppointmentStatus[] = [
  'booked',
  'confirmed',
  'arrived',
  'started',
  'completed',
  'no_show',
  'cancelled',
];

export interface AppointmentStatusProgressionProps {
  appointmentId: string;
  status: AppointmentStatus;
  /** Reflect the server-accepted status in the host surface. */
  onLocalChange: (next: AppointmentStatus) => void;
  /**
   * The booking's deposit, when the host surface knows it. Cancelling or
   * marking a no-show can FORFEIT a paid deposit per the org's policy (§4.4),
   * so those two transitions ask first. Omitted means "no deposit" — the host
   * surfaces that carry one pass it.
   */
  deposit?: DepositSummary | null;
  /**
   * Appointment start as an ISO string (real UTC instant). Only needed to tell
   * a LATE cancel from an early one. Omitting it does NOT silence the warning:
   * an unknown start cannot rule out lateness, so the forfeit dialog is shown
   * rather than skipped (see `wouldForfeitDeposit`). Every caller should pass
   * it; the fallback just refuses to fail open.
   */
  startDate?: string;
  className?: string;
}

/**
 * Shared status selector. Composed by the desktop side-panel AND the mobile
 * booking sheet so both viewports change a booking's status via the same
 * mutation. Rendered as a single Select rather than a row of buttons.
 */
export function AppointmentStatusProgression({
  appointmentId,
  status,
  onLocalChange,
  deposit,
  startDate,
  className,
}: AppointmentStatusProgressionProps) {
  const { data: organization } = useActiveOrganization();
  const { updateAppointment, isUpdating } = useUpdateAppointment({
    onSuccess: (appointment) => onLocalChange(appointment.status),
  });
  const [pendingForfeit, setPendingForfeit] = useState<ForfeitAction | null>(
    null
  );

  const apply = (next: AppointmentStatus) => {
    updateAppointment({ id: appointmentId, status: next });
  };

  const setStatus = (next: AppointmentStatus) => {
    if (next === status || isUpdating) return;

    // Cancelling or marking a no-show can take a captured deposit off the
    // customer. Confirm the AMOUNT first — but only when a forfeit would
    // genuinely happen, so the dialog stays meaningful (see
    // `wouldForfeitDeposit`). The server re-derives the same decision; this
    // only decides whether to ask.
    if (
      (next === 'cancelled' || next === 'no_show') &&
      wouldForfeitDeposit({
        action: next,
        deposit,
        startDate: startDate ?? '',
        policy: organization,
      })
    ) {
      setPendingForfeit(next);
      return;
    }

    apply(next);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium text-muted-foreground">
        {appointmentEditForm.labels.status}
      </p>
      <Select
        value={status}
        onValueChange={(next) => setStatus(next as AppointmentStatus)}
        disabled={isUpdating}
      >
        <SelectTrigger
          className="w-full"
          aria-label={appointmentEditForm.labels.status}
        >
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent className="z-[110]">
          {status === 'held' ? (
            <SelectItem value="held" disabled>
              {appointmentStatusLabels.held}
            </SelectItem>
          ) : null}
          {APPOINTMENT_STATUS_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {appointmentStatusLabels[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {pendingForfeit && deposit ? (
        <ForfeitConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setPendingForfeit(null);
          }}
          action={pendingForfeit}
          deposit={deposit}
          busy={isUpdating}
          onConfirm={() => {
            apply(pendingForfeit);
            setPendingForfeit(null);
          }}
        />
      ) : null}
    </div>
  );
}
